import { AuthError, InteractionType } from '@azure/msal-browser';
import { useMsal, useMsalAuthentication } from '@azure/msal-react';
import { FullStory, isInitialized as isFullstoryInitialized } from '@fullstory/browser';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { getFeatureFlags, IFeatureFlag } from '../queries/feature_flags/useFeatureFlags';
import analytics from '../services/analytics';
import authService from '../services/auth';
import { useAuthToken } from './entraAuthConfig';

const setupFeatureFlags = (featureFlagsData: IFeatureFlag[]) => {
  if (featureFlagsData && Object.keys(window.__amp_feature_flags).length === 0) {
    featureFlagsData.forEach(
      feature_flag => (window.__amp_feature_flags[feature_flag.feature_name] = feature_flag.enabled)
    );
  }
};

const authenticationRequest = {
  scopes: ['User.Read'],
  forceRefresh: true
};

const useRefreshTokenEveryHalfHourQuery = ({
  acquireToken,
  enabled
}: {
  acquireToken: ReturnType<typeof useMsalAuthentication>['acquireToken'];
  enabled: boolean;
}) =>
  useQuery({
    queryKey: ['msal-auth-refresh-token-query'],
    queryFn: () => acquireToken(InteractionType.Silent, authenticationRequest),
    refetchInterval: 1000 * 60 * 30,
    refetchIntervalInBackground: true,
    staleTime: 1000 * 60 * 30,
    initialData: () => null as Awaited<ReturnType<typeof acquireToken>>,
    throwOnError: false,
    enabled
  });

const EntraUserAuthenticationState = ({
  isAuthenticatedToBackend,
  setIsAuthenticatedToBackend
}: {
  isAuthenticatedToBackend: boolean;
  setIsAuthenticatedToBackend: (isAuthenticatedToBackend: boolean) => void;
}) => {
  const { instance } = useMsal();
  const [authResultFallback, setAuthResultFallback] = React.useState<Awaited<ReturnType<typeof acquireToken>>>(null);

  const navigate = useNavigate();

  const accountIdentifiers = React.useMemo(() => {
    const userEmailFromStorage = (JSON.parse(localStorage.getItem('authInfo') || '{}') as any).currentUserEmail as
      | string
      | undefined;

    if (userEmailFromStorage) {
      return {
        username: userEmailFromStorage
      };
    }
  }, []);

  // arguments MUST be stable values, see hook's implementation
  const { login, result, error, acquireToken } = useMsalAuthentication(
    InteractionType.Silent,
    authenticationRequest,
    accountIdentifiers
  );

  const { error: errorFromQuery } = useRefreshTokenEveryHalfHourQuery({
    acquireToken,
    enabled: isAuthenticatedToBackend
  });

  React.useEffect(() => {
    const derivedAuthResponse = result || authResultFallback;

    if (derivedAuthResponse) {
      instance.setActiveAccount(derivedAuthResponse.account);
      useAuthToken.setState({ idToken: derivedAuthResponse.idToken, provider: 'entra' });
      localStorage.setItem('entraIdToken', derivedAuthResponse.idToken);
    }

    if (derivedAuthResponse && !isAuthenticatedToBackend) {
      const authInAmp = async () => {
        try {
          await authService.authenticate();

          const featureFlagsData = await getFeatureFlags();
          setupFeatureFlags(featureFlagsData.feature_flags);
          setIsAuthenticatedToBackend(true);
        } catch (_) {
          setIsAuthenticatedToBackend(false);
        }
      };

      authInAmp();
    }
  }, [instance, authResultFallback, result, isAuthenticatedToBackend, setIsAuthenticatedToBackend]);

  const navigateRef = React.useRef(navigate);

  React.useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  React.useEffect(() => {
    const derivedError = error || errorFromQuery;

    if (derivedError instanceof AuthError) {
      // eslint-disable-next-line no-console
      console.error('AuthError', derivedError);
      isFullstoryInitialized() && FullStory('trackEvent', { name: 'AuthError', properties: { error: derivedError } });

      login(derivedError.errorCode === 'popup_window_error' ? InteractionType.Redirect : InteractionType.Popup, {
        ...authenticationRequest,
        prompt: 'select_account'
      })
        .then(result => {
          // eslint-disable-next-line no-console
          console.log('Authentication successful');
          setAuthResultFallback(result);
        })
        .catch(err => {
          // eslint-disable-next-line no-console
          console.error('Authentication failed', err);
          setIsAuthenticatedToBackend(false);
          navigateRef.current('/login');
        });
    }
  }, [login, error, errorFromQuery, setIsAuthenticatedToBackend, instance]);

  const location = useLocation();

  React.useEffect(() => {
    analytics.page(location);
  }, [location]);

  return <Outlet />;
};

export default EntraUserAuthenticationState;
